import { Box, Paper, Typography, styled } from '@mui/material'
import CircularProgress from '@mui/material/CircularProgress'
import React from 'react'
import { type Alias, type AliasConflict } from '../../api/types'
import { Address } from '../address/address'
import {
  Alias as AliasChip,
  ConflictAlias,
  PendingAlias
} from './desktop.alias'
import { RemoveGroupButton } from './group.actionButtons'
import { Alias as MobileAlias } from './mobile.alias'

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? '#1A2027' : '#fff',
  ...theme.typography.body2,
  padding: 0,
  textAlign: 'left',
  color: theme.palette.text.secondary,
  borderRadius: '12px',
  border: `1px solid ${theme.palette.divider}`,
  borderTop: `3px solid ${theme.palette.primary.main}`,
  boxShadow: 'none',
  overflow: 'hidden'
}))

export interface GroupItemProps {
  name: string
  email: string
  search: string
  members: string[]
  aliases: Alias[]
  allAliases: Alias[]
  pendingAlias?: string
  deletingAlias?: string
  onDeleteGroup: (email: string) => void
  onDeleteAlias: (alias: string) => void
  onAliasUpdated: (alias: Alias) => void
  onToggleAlias: (alias: Alias) => void
  conflicts: AliasConflict[]
  onResolveConflict: (alias: string, enable: boolean) => void
  isGroupDeleting?: boolean
}

const today = new Date().toISOString().split('T')[0]

export const Group: React.FC<GroupItemProps> = ({
  email,
  search,
  members,
  pendingAlias,
  deletingAlias,
  aliases,
  allAliases,
  onDeleteGroup,
  onDeleteAlias,
  onAliasUpdated,
  onToggleAlias,
  conflicts,
  onResolveConflict,
  isGroupDeleting
}) => {
  const expiredAliases = aliases.filter(
    (a) => a.expires !== '' && a.expires < today
  )
  const activeAliases = aliases.filter(
    (a) => a.expires === '' || a.expires >= today
  )

  return (
    <Item elevation={0} sx={{ mb: 2 }}>
      <Box
        sx={{
          px: 2,
          pt: 1.5,
          pb: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: (theme) =>
            theme.palette.mode === 'dark'
              ? 'rgba(255, 255, 255, 0.05)'
              : 'grey.50'
        }}
      >
        <Typography
          variant="body2"
          component="div"
          color={isGroupDeleting ? 'text.disabled' : 'primary.main'}
          sx={{
            fontFamily: 'monospace',
            fontSize: '0.8rem',
            fontWeight: 600,
            textDecoration: isGroupDeleting ? 'line-through' : 'none'
          }}
        >
          <Address value={email} search={search} />
        </Typography>
        {members?.length > 0 && (
          <Typography
            variant="caption"
            color="text.disabled"
            sx={{ display: 'block', mt: 0.25 }}
          >
            {members.map((m) => m.split('@')[0]).join(' · ')}
          </Typography>
        )}
      </Box>
      <Box sx={{ px: 1, py: 0.5 }}>
        <table className="mobile-table" style={{ width: '100%' }}>
          <tbody>
            {conflicts.length > 0 && (
              <tr>
                <td colSpan={4} style={{ padding: '4px 0 4px' }}>
                  <Box
                    sx={(theme) => ({
                      bgcolor:
                        theme.palette.mode === 'dark'
                          ? 'rgba(244, 67, 54, 0.08)'
                          : '#fff2f2',
                      border: '1px solid',
                      borderColor: 'error.light',
                      borderRadius: 1,
                      px: 1,
                      pt: 0.75,
                      pb: 0.75
                    })}
                  >
                    <Typography
                      variant="caption"
                      color="error.main"
                      sx={{
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5
                      }}
                    >
                      Conflict
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 0.75,
                        mt: 0.5
                      }}
                    >
                      {conflicts.map((conflict) => (
                        <ConflictAlias
                          key={conflict.enabled}
                          conflict={conflict}
                          search={search}
                          deletingAlias={deletingAlias}
                          onResolveConflict={onResolveConflict}
                        />
                      ))}
                    </Box>
                  </Box>
                </td>
              </tr>
            )}
            {expiredAliases.length > 0 && (
              <tr>
                <td colSpan={4} style={{ padding: '4px 0 8px' }}>
                  <Box
                    sx={(theme) => ({
                      bgcolor:
                        theme.palette.mode === 'dark'
                          ? 'rgba(255, 167, 38, 0.08)'
                          : '#fff8f0',
                      border: '1px solid',
                      borderColor: 'warning.300',
                      borderRadius: 1,
                      px: 1,
                      pt: 0.75,
                      pb: 0.75
                    })}
                  >
                    <Typography
                      variant="caption"
                      color="warning.main"
                      sx={{
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5
                      }}
                    >
                      Expired
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 0.75,
                        mt: 0.5
                      }}
                    >
                      {expiredAliases.map((alias) => (
                        <AliasChip
                          key={alias.alias}
                          alias={alias}
                          search={search}
                          deletingAlias={deletingAlias}
                          onDeleteAlias={onDeleteAlias}
                          onAliasUpdated={onAliasUpdated}
                          onToggleAlias={onToggleAlias}
                        />
                      ))}
                    </Box>
                  </Box>
                </td>
              </tr>
            )}
            {activeAliases.map((alias) => (
              <MobileAlias
                key={alias.alias}
                alias={alias}
                search={search}
                deletingAlias={deletingAlias}
                onDeleteAlias={onDeleteAlias}
                onAliasUpdated={onAliasUpdated}
                onToggleAlias={onToggleAlias}
              />
            ))}
            {pendingAlias !== undefined && (
              <tr>
                <td colSpan={3}>
                  <PendingAlias alias={pendingAlias} />
                </td>
              </tr>
            )}
            {allAliases.length === 0 && pendingAlias === undefined ? (
              <tr>
                <td className="no-alias">No email aliases</td>
                <td>
                  {isGroupDeleting ? (
                    <CircularProgress size={24} />
                  ) : (
                    <RemoveGroupButton onClick={() => onDeleteGroup(email)} />
                  )}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </Box>
    </Item>
  )
}
